import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { localizedProps } from "src/utils/localize";
import AutoGallery, { tourImages } from "src/sections/components/auto-gallery";
import { formatDateRangeShort } from "src/utils/dates";

// ЗАГЛУШКА ВМЕСТО ФОТО.
//
// Пока к туру не загрузили снимки, на месте фотографии остаётся цветное
// поле. Раньше здесь стояли шесть готовых градиентов, доставшихся от
// прежнего проекта: холодная бирюза, морская синь, болотная зелень. На
// тёплой сливовой палитре они выглядели как чужие плитки из другого шаблона
// — ровно тот «дешёвый вид», от которого мы уходим.
//
// Теперь градиенты собраны из переменных палитры, поэтому заглушка всегда
// в тон сайту и меняется вместе с сезоном (сакура ↔ момидзи). Шесть
// вариантов — чтобы соседние карточки в ленте не выглядели одинаково.
const GRADIENTS = [
  "linear-gradient(145deg, rgb(var(--kj-gold) / 0.55), rgb(var(--kj-card)))",
  "linear-gradient(145deg, rgb(var(--kj-sky) / 0.5), rgb(var(--kj-line)))",
  "linear-gradient(145deg, rgb(var(--kj-line)), rgb(var(--kj-gold) / 0.4))",
  "linear-gradient(145deg, rgb(var(--kj-card)), rgb(var(--kj-sky) / 0.45))",
  "linear-gradient(145deg, rgb(var(--kj-gold) / 0.35), rgb(var(--kj-line)))",
  "linear-gradient(145deg, rgb(var(--kj-line)), rgb(var(--kj-sky) / 0.3))",
];

export const TourCard = ({ activity, index = 0 }) => {
  const { i18n } = useTranslation();

  const lang = i18n.language;
  const name = localizedProps(activity?.name, lang);
  const place = localizedProps(
    activity?.place || activity?.category,
    lang
  );
  const images = tourImages(activity);
  // Маршрут на карточке. Места мало, поэтому полноценную схему линии (как
  // на странице тура) рисовать негде — но и простая строка через тире
  // выглядела рядовым текстом. Компромисс: города остаются списком, а между
  // ними стоит маленький ромб цвета линии — тот же знак, что и точки на
  // схеме внутри. Список обрезается по ширине, целиком виден в подсказке.
  const stops = (activity?.route || [])
    .map((stop) => localizedProps(stop, lang).value)
    .filter(Boolean);
  const routeTitle = stops.join(" – ");
  const dates = formatDateRangeShort(activity?.date, activity?.dateEnd, lang);

  // Карточка была <button onClick={navigate}> — то есть ссылкой, которую нельзя
  // открыть в новой вкладке, скопировать адресом и которую не видит поисковик.
  // Теперь это настоящая ссылка; переход по ней остаётся клиентским.
  return (
    <Link
      to={activity?._id ? `/activity/${activity._id}` : "#"}
      className="group text-left w-full h-full flex flex-col bg-kj-card border border-kj-line rounded-2xl overflow-hidden transition-transform duration-200 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark"
    >
      <div
        className="h-[170px] sm:h-[200px] relative overflow-hidden"
        style={{ background: GRADIENTS[index % GRADIENTS.length] }}
      >
        {/* Без фотографий пустое цветное поле выглядит как ошибка загрузки.
            Орнамент сэйгайха превращает его в осознанную заставку. */}
        {images.length === 0 && (
          <span
            aria-hidden="true"
            style={{ "--pattern-alpha": 0.2 }}
            className="absolute inset-0 pattern-seigaiha"
          />
        )}
        <AutoGallery
          images={images}
          alt={name.value}
          interval={3500 + (index % 4) * 400}
          interactiveDots={false}
        />
      </div>

      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div
          {...place.attrs}
          className="text-kj-sub text-xs font-bold uppercase tracking-[0.1em] line-clamp-1"
        >
          {place.value}
        </div>
        {/* Три строки, а не две: русские и казахские названия туров заметно
            длиннее английских, и «Осенние каникулы в Японии — кампус-тур по
            университетам» обрывался многоточием на самом важном слове.
            text-balance разводит строки поровну, чтобы последняя не висела
            одним словом. title="" оставлен на случай совсем длинных имён. */}
        <h4
          {...name.attrs}
          title={name.value}
          className="mt-1.5 text-base sm:text-lg font-extrabold uppercase tracking-tight text-kj-ink line-clamp-3 text-balance"
        >
          {name.value}
        </h4>
        {stops.length > 0 && (
          <p
            className="mt-2 flex items-center gap-x-1.5 overflow-hidden text-sm text-kj-sub"
            title={routeTitle}
          >
            {stops.map((stop, i) => (
              <span key={`${stop}-${i}`} className="flex items-center gap-x-1.5 whitespace-nowrap">
                {i > 0 && (
                  <span
                    aria-hidden="true"
                    className="h-1 w-1 flex-none rotate-45 bg-kj-sky/80"
                  />
                )}
                {stop}
              </span>
            ))}
          </p>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2 border-t border-kj-line">
          <div className="min-w-0 truncate font-extrabold text-kj-gold text-sm sm:text-base">
            {activity?.price || ""}
            {dates ? (
              <span className="text-kj-sub font-semibold text-xs ml-1">
                / {dates}
              </span>
            ) : null}
          </div>
          <span
            aria-hidden="true"
            className="text-kj-sky text-lg transition-transform group-hover:translate-x-1"
          >
            →
          </span>
        </div>
      </div>
    </Link>
  );
};
